<template>
  <vf-dialog-layout :class-wrapper="classDialogWrapper" hide-close-button hide-header>
    <section v-if="!closeOnAdd" data-scroll-el="notificationSection">
      <vf-notification
        v-if="cart.notification.message"
        class="mb-2 mt-3 md:mb-4 md:mt-2"
        :type="cart.notification.type"
        @close="cart.notification.message = ''"
      >
        {{ cart.notification.message }}
        <vf-link v-if="parentInteractionOrigin === 'shop-the-look'" class="text-base" to="/cart">
          {{ $t.viewCart }}
        </vf-link>
      </vf-notification>
    </section>

    <div class="p-6" @keydown.esc="emit('resolve')">
      <define-product-info>
        <div class="flex items-start gap-4">
          <div class="grow">
            <div v-if="$feature.showSkeletonsOnQuickshop && pending" class="mb-3 h-2 w-18 skeleton" />
            <template v-else-if="details?.eyebrow?.label">
              <vf-toggletip
                v-if="details.eyebrow.details"
                placement="bottom-end"
                :props-trigger="{ class: 'mb-1 mt-2 flex items-center gap-1' }"
              >
                <template #trigger>
                  <p v-bind="eyebrowStyle" class="truncate eyebrow">
                    {{ details.eyebrow.label }}
                  </p>
                  <vf-icon name="info" />
                </template>
                {{ details.eyebrow.details }}
              </vf-toggletip>
              <p
                v-else
                v-bind="eyebrowStyle"
                class="mb-1 mt-2 truncate eyebrow"
              >
                {{ details.eyebrow.label }}
              </p>
            </template>
            <div v-if="$feature.showSkeletonsOnQuickshop && pending" class="mb-3 mt-5 h-3 w-40 skeleton" />
            <base-button
              v-else-if="details?.name"
              class="line-clamp-3 cursor-pointer text-left"
              :class="[brandClasses.productName, classProductName]"
              @click="viewDetails"
            >
              {{ details.name }}
            </base-button>
          </div>
          <base-button
            :aria-label="$t.close"
            class="shrink-0"
            @click="emit('resolve')"
          >
            <vf-icon name="close" size="md" />
          </base-button>
        </div>
      </define-product-info>

      <!-- DETAILED: thumbnail left + info right -->
      <template v-if="variant === 'detailed'">
        <div class="flex gap-4">
          <!-- Image thumbnail (clickable → PDP) -->
          <div>
            <slot name="gallery">
              <div
                v-if="$feature.showSkeletonsOnQuickshop && pending"
                class="mb-4 skeleton"
                :style="{ 'aspect-ratio': galleryAspectRatio }"
              />
              <div
                v-else-if="activeGalleryImage"
                class="cursor-pointer"
                @click="viewDetails"
              >
                <base-picture
                  :alt="activeGalleryImage.alt || `${details?.name} - 1`"
                  class="w-full"
                  :height="gallery.presets.src.height"
                  :props-img="{ fetchpriority: 'high' }"
                  :src="getImageTransformations(activeGalleryImage.src, {
                    ...gallery.presets.src,
                    assetTransformation: activeGalleryImage?.assetTransformation,
                  })"
                  :width="gallery.presets.src.width"
                />
              </div>
            </slot>
            <slot name="belowGallery" />
          </div>

          <!-- Info column -->
          <div class="min-w-0 flex-1">
            <product-info />

            <p v-if="!pending && colorAttributes.length" class="mt-1 text-sm">
              <span :class="brandClasses.label">{{ colorAttributes[0].label }}:</span>
              {{ attributeSelectionLabels.color?.value }}
            </p>

            <base-button
              v-if="!pending && url"
              class="mt-2 cursor-pointer text-left text-sm underlined"
              @click="viewDetails"
            >
              {{ $t.viewDetails }}
            </base-button>

            <div
              v-if="$feature.showSkeletonsOnQuickshop && pending && details?.price"
              class="mb-2 mt-3 h-2 w-18 skeleton"
            />
            <product-pricing
              v-else-if="!pending && !isDigitalProduct"
              class="mt-4"
              class-wrapper="!overline-1"
              :currency="(details?.currency || product?.currency)!"
              :price="productPrice"
              :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
              :show-omnibus-badge="$feature.showOmnibusBadgeOnQuickshopAndPDP"
            />

            <slot name="belowPrice" :product="details" :product-price />
          </div>
        </div>
      </template>

      <!-- SIMPLIFIED: single column, no image -->
      <template v-else>
        <div>
          <product-info />

          <p v-if="!pending && colorAttributes.length" class="mt-2" :class="brandClasses.label">
            <span>{{ colorAttributes[0].label }}:</span>
            {{ attributeSelectionLabels.color?.value }}
          </p>

          <base-button
            v-if="!pending && url"
            class="mt-2 cursor-pointer underlined"
            :class="brandClasses.label"
            @click="viewDetails"
          >
            {{ $t.viewDetails }}
          </base-button>

          <div
            v-if="$feature.showSkeletonsOnQuickshop && pending && details?.price"
            class="mb-2 mt-3 h-2 w-18 skeleton"
          />
          <product-pricing
            v-else-if="!pending && !isDigitalProduct"
            class="mt-4"
            class-wrapper="!overline-1"
            :currency="(details?.currency || product?.currency)!"
            :price="productPrice"
            :show-discount-percentage="$feature.showDiscountPercentageOnCatalog"
            :show-omnibus-badge="$feature.showOmnibusBadgeOnQuickshopAndPDP"
          />

          <slot name="belowPrice" :product="details" :product-price />
        </div>
      </template>

      <!-- SIZE SELECTION — full width for both variants -->
      <div class="mt-6">
        <template v-if="pending && $feature.showSkeletonsOnQuickshop">
          <div>
            <div class="mb-3 mt-5 h-3 w-11 skeleton" />
            <div class="mt-2 flex gap-3 wrap">
              <div v-for="_size in 12" :key="_size" class="h-10 w-10 skeleton" />
            </div>
            <div class="mb-3 mt-5 h-3 w-11 skeleton" />
            <div class="h-10 max-w-full w-82 skeleton" />
          </div>
        </template>
        <div v-else-if="details && isDigitalProduct" ref="sizePickersEl">
          <form-gift-card
            ref="formRef"
            v-model:attribute-selection="attributeSelection"
            v-model:form="giftCardFormFields"
            :attribute-selection-labels
            :error
            :hide-checkbox="!!context"
            hide-submit
            :pending="detailsPending"
            :product="details"
            @select-card-amount="selectSize"
            @select-card-style="(id, _, label) => selectColor(id, label)"
          />
        </div>
        <template v-else>
          <div v-if="product && isComingSoon" class="space-y-3">
            <p class="title-4 c-grey-20">
              {{ comingSoonText }}
            </p>
            <p v-if="selectedVariant?.notifyMe">
              {{ replaceAll($t.comingSoonDescription, { productName: product.name }) }}
            </p>
          </div>
          <div
            v-else
            ref="sizePickersEl"
            class="f-col gap-y-3"
          >
            <base-form
              ref="formAttributesRef"
              class="space-y-3"
              :form="attributeSelection"
              :scroll-to-error="false"
            >
              <template v-if="$feature.enableColorVariantsForQuickshop && variant === 'detailed'">
                <fieldset
                  v-for="({ label, options, type }, i) in colorAttributes"
                  :key="type"
                  :aria-labelledby="`qs-${type}-${i}`"
                >
                  <p :id="`qs-${type}-${i}`" class="body-1 fw-bold">
                    {{ label }}: {{ attributeSelectionLabels[type]?.value }}
                  </p>
                  <div class="mt-4 flex wrap" :class="{ 'gap-1': swatches.variant === 'rectangle' }">
                    <vf-color-picker
                      v-for="{ available, id: optionId, label: optionLabel, swatch, value } in options"
                      :key="optionLabel"
                      :active="!getIsPending(value, optionId) && getIsActive(value, optionId)"
                      :color="swatch?.color"
                      :loading="getIsPending(value, optionId)"
                      :name="optionLabel"
                      :size="swatches.size"
                      :thumbnail="swatch?.image"
                      :unavailable="!isComingSoon && (!available && !getIsPending(value, optionId))"
                      :variant="swatches.variant"
                      @click="selectColor(optionId, optionLabel)"
                    />
                  </div>
                </fieldset>
              </template>
              <template v-for="({ label, options, type }, i) of sizeAttributes" :key="i">
                <div>
                  <base-form-field v-slot="{ attrs, invalid }" :name="type" :rule="validateRequired()">
                    <fieldset :aria-labelledby="`qs-${type}-${i}`">
                      <span class="pt-2 overline-1">
                        <span :class="brandClasses.label">
                          <span :id="`qs-${type}-${i}`">{{ label }}</span>:
                        </span>
                        {{ attributeSelectionLabels[type]?.value }}
                        <span v-if="type !== 'color' && showStockStatusLabel">
                          - <span class="c-red-30">{{ selectedVariant?.stockStatusLabel }}</span>
                        </span>
                      </span>
                      <vf-form-error v-if="error && invalid" class="mt-1">
                        {{ replaceAll($t.selectSizeError, { label: label.toLowerCase() }) }}
                      </vf-form-error>
                      <div class="mt-2 flex gap-2 wrap">
                        <vf-size-picker
                          v-for="({ available, label: optionLabel, value }, j) in options"
                          :key="value"
                          :aria-disabled="!available || (!allowOutOfStockSelection && !getAttrOptionAvailable(type, value) && !getAttrOptionNotifyMe(type, value))"
                          :class="{ 'pointer-events-none':
                            !allowOutOfStockSelection
                            && !getAttrOptionAvailable(type, value)
                            && !getAttrOptionNotifyMe(type, value),
                          }"
                          :model-value="attributeSelection[type]"
                          :name="`${i}-${details?.id || product?.id}-quickshop`"
                          :required="!j"
                          size="sm"
                          :type
                          :unavailable="!available || !getAttrOptionAvailable(type, value)"
                          :value
                          v-bind="attrs"
                          @input="selectSize(type, value, optionLabel)"
                        >
                          {{ optionLabel }}
                        </vf-size-picker>
                      </div>
                    </fieldset>
                  </base-form-field>
                </div>
              </template>
            </base-form>
          </div>
        </template>
      </div>
    </div>

    <!-- STICKY FOOTER: Add to Cart only -->
    <base-sticky name="footerQuickshop" position="bottom">
      <div
        class="rounded-xl bg-grey-90 bg-op-70 p-4 shadow-xl backdrop-blur-md"
      >
        <product-cta
          :add-to-cart-label="context === 'bundle-selection' ? $t.save : undefined"
          :attribute-selection
          class="w-full"
          :enable-color-as-attribute
          :force-sign-in-to-buy
          :include-coming-soon="$feature.showComingSoonOnQuickshop"
          :include-notify-me="$feature.showNotifyMeOnQuickshop"
          :include-update-in-cart="context === 'update-cart-item' || context === 'update-saved-item'"
          :loading="loading || pending"
          :notify-me-label="$t.moreDetails"
          :product="details"
          :size="ctaSize"
          @add-to-cart="handleAddToCart"
          @notify-me="viewDetails"
          @update-in-cart="updateInCart"
        />
        <slot :attribute-selection name="viewDetailsCta" :product="details" />
      </div>
    </base-sticky>
  </vf-dialog-layout>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType, FormGiftCard as FormGiftCardType } from '#components'
import type { Product, ProductPrice, ProductVariants } from '#root/api/clients/product/data-contracts'
import type { GiftOption } from '#types/cart'
import type { CSSClass } from '#types/common'
import type { InteractionOrigin, ProductContext, ShippingMethodContext } from '#types/gtm'
import type { BundleSelectionItem, GiftCardFormFields, ProductUpsell } from '#types/product'
import type { LocationQueryRaw } from 'vue-router'
import { VfDialogLayout } from '#components'
import { ApiErrorCode } from '#root/enums/api'

export type Props = {
  categoryURL?: string
  classDialogWrapper?: CSSClass
  classProductName?: CSSClass
  closeOnAdd?: boolean
  // flag to determine the CTA's label/behavior: cart-item edit, saved-item edit, or staging a bundle pick
  context?: 'update-cart-item' | 'update-saved-item' | 'bundle-selection'
  forceSignInToBuy?: boolean
  giftOption?: GiftOption
  gtmConfig?: ProductContext & ShippingMethodContext & { appSource?: InteractionOrigin }
  initialAttributes?: ProductVariants[number]['attributes']
  isUpsell?: boolean
  product?: Product
  productId?: string
  storeId?: string
  suppressMiniCart?: boolean
  title?: string
  variant?: 'detailed' | 'simplified'
  updateProductId?: (id: string) => void
}
export type Emits = {
  resolve: [selection?: BundleSelectionItem]
}

const {
  categoryURL,
  classDialogWrapper = '!p-0',
  closeOnAdd,
  context,
  forceSignInToBuy,
  giftOption,
  gtmConfig,
  initialAttributes,
  product,
  storeId,
  suppressMiniCart,
  variant = 'simplified'
} = defineProps<Props>()

const emit = defineEmits<Emits>()

const productId = defineModel<string>('productId')

const [DefineProductInfo, ProductInfo] = createReusableTemplate()
const { DialogSignIn, DialogLoyaltySignUp } = useDialogsStore()

// Global
const config = useAppConfig()
const {
  brandClasses,
  ctaSize,
  gallery
} = config.components.dialog.quickshop
const { maxCarouselItems } = config.components.product.card
const { allowOutOfStockSelection, enableColorAsAttribute, swatches } = config.pages.pdp
const auth = useAuthStore()
const cart = useCartStore()
const { getComingSoonText, getIsComingSoonProduct } = useComingSoon()
const { validateRequired } = useLocalizedValidators()
const { $feature, $gtm, $t, $viewport } = useNuxtApp()
const { trackEvent } = useOptimizely()
const { setProductDetailViewTracking, updateSavedItemInCart, updateItemInCart } = useQuickshop()
const route = useRoute()
const router = useRouter()
const gtmTrace = useSessionStorage('gtmTrace', {} as Record<string, ShippingMethodContext>)
const toast = useToaster()
const user = useUserStore()

// Local
const galleryAspectRatio = gallery.presets.src.width / gallery.presets.src.height
const attributeSelection = reactive(initialAttributes || {}) as unknown as ProductVariants[number]['attributes']
const activeProduct = computed(() => productId.value || product?.id || '')
const error = ref(false)
const filtersQuery = (() => {
  try {
    return JSON.parse(route.query.filters as string || '{}')
  }
  catch (e) {
    console.error('Failed to parse filters from query:', e)
    return {}
  }
})()
const loading = ref(false)
const sizePickersEl = ref<HTMLElement>()
const parentInteractionOrigin = ref(getInteractionOrigin())
const formRef = ref<InstanceType<typeof FormGiftCardType>>()
const formAttributesRef = ref<InstanceType<typeof BaseFormType> | null>(null)

const giftCardFormFields = ref<GiftCardFormFields>({
  email: giftOption?.email || '',
  fullName: giftOption?.to || '',
  message: giftOption?.message || '',
  amount: (giftOption && initialAttributes?.size === 'X' && product?.price.lowCurrent.toString()) || '',
  size: attributeSelection?.size || ''
})

// Fetch
const {
  productPending: detailsPending,
  productError,
  inventoryPending,
  inventory,
  product: details
} = await useProduct(activeProduct, {
  enableColorAsAttribute,
  server: false,
  // @ts-expect-error incorrect Nuxt types
  default: () => (product),
  // Temporary fix for missing data from API
  transform: (input) => ({
    ...input,
    eyebrow: input.eyebrow || product?.eyebrow,
    badge: input.badge || product?.badge,
    ...(maxCarouselItems && { gallery: input.gallery?.slice(0, maxCarouselItems) || [] }),
    altGalleries: input.altGalleries || product?.altGalleries
  }),
  key: `quickshop-${activeProduct.value}`,
  onResponse: async ({ response: { _data } }) => productId.value = _data.id,
  onResponseError: ({ response: { _data } }) => {
    log.error(_data)
    emit('resolve')
  }
})

const { activeGallery } = useActiveGallery(details)
const activeGalleryImage = computed(() => activeGallery.value.find(({ type }) => type === 'image'))

const attributeSelectionLabels = useAttributeSelectionLabels(details, attributeSelection)
const { getAttrOptionAvailable, getAttrOptionNotifyMe } = getProductAvailability(attributeSelection, details)
const url = computed(() => details.value?.url || product?.url)
const isDigitalProduct = computed(() => details.value?.productType?.toLowerCase() === 'digital')
const isComingSoon = computed(() => $feature.showComingSoonOnQuickshop
  && getIsComingSoonProduct(details.value?.badge, details.value?.eyebrow))
const isNonLoyaltyMember = computed(() => !!(auth.loggedIn && !user.isLoyaltyMember))
const isSignInToBuy = computed(() =>
  (forceSignInToBuy && !auth.loggedIn)
  || (details.value?.presale && (!auth.loggedIn || isNonLoyaltyMember.value)))
const eyebrowStyle = computed(() => {
  const colorHexCode = details?.value?.eyebrow?.colorHexCode
  return colorHexCode && colorHexCode !== 'None' ? { style: `color: #${colorHexCode}` } : {}
})

const pending = computed(() => detailsPending?.value || inventoryPending?.value)

const variants = computed(() => isArray(details.value?.variants) ? details.value.variants : [])

// Attribute selection from filtersQuery
const initialFilterSelection = computed(() => {
  const initialSelection = {}
  Object.keys(filtersQuery).forEach((label) => {
    // Find attribute by label, URL filters use label instead of type
    const attribute = details.value?.attributes.find((attribute) => {
      const attributeLabel = attribute.label?.toLowerCase()
      const filterLabel = label?.toLowerCase()

      return filterLabel?.includes(attributeLabel) || attributeLabel?.includes(filterLabel!)
    })

    if (attribute) {
      const option = attribute?.options.find((option) => {
        // Select if there is only one filter option
        const [filterOption] = filtersQuery?.[label]
        return option.label?.toLowerCase() === filterOption?.toLowerCase() && filtersQuery[label].length === 1
      })
      // Set initial selection for an attribute type
      if (option)
        initialSelection[attribute.type] = option.value
    }
  })
  return initialSelection
})

const attributes = computed(() => details.value?.attributes || [])

const colorAttributes = computed(() => attributes.value.filter(({ type }) => type === 'color'))
const sizeAttributes = computed(() =>
  attributes.value
    .filter(({ type }) => type !== 'color')
    .map((attr) => ({ ...attr, options: attr.options.filter(({ productUrl }) => !productUrl) }))
    .filter(({ options }) => options.length > 0)
)
const isVariantSelected = computed(() => attributes.value.every(({ type }) =>
  attributeSelection[type]
  || type === 'color'
  || (type === 'size' && giftCardFormFields.value.amount))
)
const selectedVariant = computed(() => {
  const variant = variants.value.find((variant) =>
    Object.entries(variant.attributes)?.every(
      ([key, val]) =>
        attributeSelection[key] === val
        || (key === 'size' && val === 'X' && giftCardFormFields.value.amount)
    )
  )
  return variant && isVariantSelected.value ? variant : null
})

const getIsActive = (value: string, id: string) =>
  enableColorAsAttribute ? attributeSelection.color === value : activeProduct.value === id

const getIsPending = (value: string, id: string) => {
  if (!pending.value) return false

  return enableColorAsAttribute
    ? attributeSelection.color === value && id !== details.value?.id
    : productId.value === id
}

const productPrice = computed<ProductPrice>(() => {
  if (selectedVariant.value?.price) {
    const { current, original, historicBestPrice, percentagePriceDiscount } = selectedVariant.value.price
    return {
      highCurrent: current ?? original,
      lowCurrent: current ?? original,
      highOriginal: original,
      lowOriginal: original,
      historicBestPrice,
      percentagePriceDiscount
    }
  }
  return details.value?.price || product?.price || { highCurrent: 0, lowCurrent: 0, highOriginal: 0, lowOriginal: 0 }
})

const itemGiftOption = computed(() => isDigitalProduct.value
  ? {
      to: giftCardFormFields.value.fullName,
      email: giftCardFormFields.value.email,
      message: giftCardFormFields.value.message
    }
  : undefined
)
const itemCustomAmount = computed(() => giftCardFormFields.value.amount
  ? {
      amount: +giftCardFormFields.value.amount
    }
  : undefined
)

const comingSoonText = computed(() => getComingSoonText(product?.targetPublishDate ?? details.value?.targetPublishDate))

async function addToCart() {
  error.value = (formRef.value && !formRef.value?.validate(true)) || !isVariantSelected.value

  if (error.value || !selectedVariant.value) {
    sizePickersEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    formAttributesRef.value?.validate(true)
    return
  }

  loading.value = true

  if (selectedVariant.value.id) {
    setGtmCartProductsMap(selectedVariant.value.id, {
      ...details.value,
      bundleId: gtmConfig?.bundleId,
      category: gtmConfig?.category,
      list: gtmConfig?.list
    })
  }

  try {
    const { shippingMethod, shippingStoreId, shippingWindow } = gtmConfig || {}

    if (shippingMethod && shippingStoreId && shippingWindow)
      gtmTrace.value[selectedVariant.value!.id] = { shippingMethod, shippingStoreId, shippingWindow }

    await withRetryAfterTime(() => cart.add({
      productId: selectedVariant.value!.id,
      upc: selectedVariant.value!.upc,
      maxQty: inventory.value?.variants[selectedVariant.value!.id]?.quantity,
      productImageURL: activeGalleryImage.value?.thumbnail ?? activeGalleryImage.value?.src,
    }, {
      suppressMiniCart,
      giftOption: itemGiftOption.value,
      customAmount: itemCustomAmount.value,
      extendedGtmConfig: {
        ...gtmConfig,
        category: gtmConfig?.category || interactionOriginMap[gtmConfig?.appSource!]
      },
      storeId: shippingStoreId
    }), 2000, [ApiErrorCode.GWP_PRODUCT])
    trackEvent('quick_shop', {
      product: details.value?.id || activeProduct.value
    })
    emit('resolve')
  }
  catch (err) {
    log.error(err)
    delete gtmTrace.value[selectedVariant.value!.id]
  }

  loading.value = false
  resetInteractionOrigin()

  if (cart.errorMessage) {
    cart.setNotification('error', cart.errorMessage)
    if (selectedVariant.value.id && details.value) removeGtmCartProductsMap(selectedVariant.value.id, details.value.id)
  }
  else {
    formRef.value?.reset()
  }

  if (!$feature.showMiniCartOnAddProduct || closeOnAdd)
    emit('resolve')
}

// 'bundle-selection' context: don't add to cart here, just hand the picked variant back to the
// caller (usePdp().openQuickshop) - the actual cart.add() happens once, batched, from
// the PDP's own Add to Cart button.
const saveBundleSelection = () => {
  error.value = (formRef.value && !formRef.value?.validate(true)) || !isVariantSelected.value

  if (error.value || !selectedVariant.value || !product) {
    sizePickersEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    formAttributesRef.value?.validate(true)
    return
  }

  emit('resolve', {
    attributes: selectedVariant.value.attributes,
    attributeLabels: attributeSelectionLabels.value,
    product: {
      ...(product as unknown as ProductUpsell),
      image: activeGalleryImage.value?.src ?? (product as unknown as ProductUpsell).image
    },
    productId: selectedVariant.value.id,
    qty: 1,
    upc: selectedVariant.value.upc!
  })
}

const updateInCart = async () => {
  loading.value = true
  error.value = (formRef.value && !formRef.value?.validate(true)) || !isVariantSelected.value
  try {
    if (error.value || !selectedVariant.value || !product?.itemId) {
      sizePickersEl.value?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
      formAttributesRef.value?.validate(true)
      return
    }

    if (context === 'update-cart-item') {
      const { itemId = '' } = product || {}
      const currentItem = itemId && cart.items.find(({ id }) => id === itemId)

      if (selectedVariant.value.id)
        setGtmCartProductsMap(selectedVariant.value.id, { ...details.value, list: gtmConfig?.list })

      if (currentItem)
        setGtmCartProductVariant(selectedVariant.value.id, getGTMProduct(currentItem.productId))

      await updateItemInCart(
        product!.itemId as string,
        product!.id as string,
        activeProduct.value,
        selectedVariant.value!.id,
        selectedVariant.value!.upc,
        itemGiftOption.value,
        itemCustomAmount.value,
        storeId
      )
    }

    if (context === 'update-saved-item')
      await updateSavedItemInCart(product!.itemId as string, selectedVariant.value!.id as string, url.value)

    await toast.add({
      props: {
        title: $t.success,
        message: replaceAll($t.cartItemEdited, { name: details.value?.name }),
        type: 'success',
      },
      promise: true
    })

    emit('resolve')
  }
  catch (err) {
    assertApiError(err)
    error.value = true

    switch (err.errorId) {
      case ApiErrorCode.ADDED_MAX_QUANTITY:
        toast.add({
          props: {
            type: 'error',
            message: cart.getMaxAllowedQtyMessage(err)
          }
        })
        emit('resolve')
        break
      case ApiErrorCode.ITEM_OOS:
      case ApiErrorCode.ITEMS_OOS:
        cart.setNotification('error', err.message || $t.unableToUpdateCartItem, 'section')
        break
    }
  }
  finally {
    loading.value = false
  }
}

const signInToBuy = async () => {
  if (isNonLoyaltyMember.value) {
    await DialogLoyaltySignUp
      .keep($viewport.lg)
      .open({ formLocation: 'modal:single:myaccount:none', isSignInToBuy: true }, { depth: 'lg' })
  }
  else {
    await DialogSignIn
      .keep($viewport.lg)
      .open({ formLocation: 'modal:single:pdp:none', isSignInToBuy: true }, { depth: 'lg' })
  }

  if (auth.loggedIn && !isNonLoyaltyMember.value) {
    await until(() => cart.pending).toBe(false)
    context === 'update-cart-item' ? updateInCart() : addToCart()
  }
}

const handleAddToCart = () => {
  if (context === 'bundle-selection') saveBundleSelection()
  else if (isSignInToBuy.value) signInToBuy()
  else addToCart()
}

const preselectAttributes = () => {
  attributes.value.forEach(({ type, options }) => {
    const availableOptions = options.filter(({ available }) => available)
    if (type === 'color') {
      const colorSelection = colorAttributes.value[0].options.find(({ id }) => id === activeProduct.value)?.value
      const defaultColor = (options?.find(({ defaultColor }) => defaultColor) || options[0])?.value

      attributeSelection.color = colorSelection || defaultColor
    }
    else if (options.length === 1) {
      attributeSelection[type] = options[0].value
    }
    else if (type === 'fitType') {
      attributeSelection[type] = options?.[0]?.value || options?.[0]?.label
    }
    else {
      attributeSelection[type] = initialAttributes?.[type] || initialFilterSelection.value[type] || attributeSelection[type] || ''
    }

    if (!attributeSelection[type] && $feature.enablePreselectFirstWidth && type === 'width')
      attributeSelection[type] = availableOptions[0]?.value || options[0].value
  })
  formAttributesRef.value?.validate()
}

const selectColor = (id: string, label: string) => {
  const colorOption = colorAttributes.value[0]?.options.find((opt) => opt.id === id)
  attributeSelection.color = colorOption?.value ?? label
  productId.value = id

  const eventLabel = `${id} - ${label}`

  if (parentInteractionOrigin.value === 'shop-the-look')
    $gtm.push('shopTheLook.onSelectColor', eventLabel, { bundleId: gtmConfig!.bundleId! })
  else if (parentInteractionOrigin.value === 'cart')
    $gtm.push('cart.onSelectColor', eventLabel)
  else
    $gtm.push('plpPage.onSelectColor', eventLabel)
}

const selectSize = (type: string, value: string, label: string) => {
  attributeSelection[type] = value
  giftCardFormFields[type] = value

  const eventLabel = `${activeProduct.value} - ${label}`

  if (parentInteractionOrigin.value === 'shop-the-look')
    $gtm.push('shopTheLook.onSelectSize', eventLabel, { bundleId: gtmConfig!.bundleId! })
  else if (parentInteractionOrigin.value === 'cart')
    $gtm.push('cart.onSelectSize', eventLabel)
  else
    $gtm.push('plpPage.onSelectSize', eventLabel)
}

const viewDetails = async () => {
  const hs = history?.state ?? {}
  $gtm.push('product.onProductClick', (details?.value || product)!, {
    action: 'Click View Details',
    breadcrumbs: deserializeAnalyticsBreadcrumbs(hs.breadcrumbs) || [],
    category: interactionOriginMap[gtmConfig?.appSource!] || gtmConfig?.category || hs.category,
    list: gtmConfig?.list,
    searchTerm: route.query.q?.toString()
  })

  emit('resolve')

  const selection = Object.entries(attributeSelection).filter(([key, value]) =>
    enableColorAsAttribute ? value : key !== 'color' && value
  )

  await router.push({
    path: url.value,
    state: {
      breadcrumbs: hs.breadcrumbs,
      category: interactionOriginMap[gtmConfig?.appSource!] || gtmConfig?.category || hs.category,
      categoryURL,
      list: gtmConfig?.list,
      searchTerm: route.query.q?.toString()
    },
    query: {
      ...(selection.length && {
        ...Object.fromEntries(selection)
      })
    } as unknown as LocationQueryRaw
  })
}

const showStockStatusLabel = computed<boolean>(() =>
  $feature.showSizeLevelStockMessagingOnPDP && !!selectedVariant.value?.stockStatusLabel)

setProductDetailViewTracking(
  activeProduct,
  details,
  detailsPending,
  inventory,
  inventoryPending,
  productError,
  gtmConfig
)

onMounted(() => {
  cart.clearNotification()

  if (interactionOriginMap[gtmConfig?.appSource!]) {
    setInteractionOrigin(gtmConfig!.appSource, true)
  }
  else {
    setInteractionOrigin()
  }

  parentInteractionOrigin.value = getInteractionOrigin()
})

onUnmounted(() => {
  if (!closeOnAdd)
    cart.clearNotification()

  if (interactionOriginMap[gtmConfig?.appSource!])
    setInteractionOrigin(parentInteractionOrigin.value || '')
})

if (!closeOnAdd) {
  watch(cart.notification, () => {
    if (cart.notification.message)
      scrollToElement('notificationSection')
  })
}

whenever(() => !pending.value, () => {
  preselectAttributes()
})
</script>
